/**
 * iter 49 — Unified "All Organizations" dashboard.
 *
 * Replaces the separate Customers and Vendors pages. One page covers:
 *   - Listing every external org via the v_my_network DB view
 *   - Filter chips ALL / CUSTOMER / VENDOR / PARTNER + Core highlight
 *   - "+ NEW" menu  (Add Customer / New Vendor / Invite Customer Org)
 *   - Row click → unified detail drawer
 *       · Customer-side fields (name/email/phone/address/proof recipients/tags)
 *       · Vendor-side fields (vendor contact, claim status, invite controls,
 *         active toggle, delete)
 *       · For Partner rows both panels render in tabs
 *
 * Backed by:
 *   GET    /api/network                  (the unified list view)
 *   GET    /api/customers, /api/vendors  (full detail records, looked up by id)
 *   POST   /api/customers                (create customer)
 *   PUT    /api/customers/:id            (edit)
 *   DELETE /api/customers/:id
 *   POST   /api/customer/:id/tags
 *   POST   /api/customers/:id/proof-recipients  ← via PUT body
 *   POST   /api/orgs/invitations         (invite customer ORG)
 *   POST   /api/vendors                  (create vendor)
 *   PATCH  /api/vendors/:id              (toggle is_active)
 *   POST   /api/vendors/:id/invite       (send/resend invite)
 *   POST   /api/vendors/:id/cancel-invite
 *   DELETE /api/vendors/:id
 *   POST   /api/vendor_link/:id/tags
 */
import { useEffect, useState, useCallback } from "react";
import {
  Buildings, Toolbox, ArrowsLeftRight, Star, Plus, EnvelopeSimple,
  Copy, CheckCircle, Warning, ArrowsClockwise, X as XIcon, Clock, CaretDown,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fmtDate, formatErr } from "../lib/api";
import { Drawer, Field } from "../components/UI";
import { Page, Skeleton } from "../components/AppShell";
import { TagPicker, TagChipList } from "../components/TagUI";
import ListControls from "../components/ListControls";
import { useAuth } from "../lib/auth";
import OrgAdminDrawer from "../components/OrgAdmin/OrgAdminDrawer";
import CustomerFormDrawer from "../components/CustomerFormDrawer";

const REL_CHIPS = [
  { key: "all",      label: "ALL" },
  { key: "customer", label: "CUSTOMER" },
  { key: "vendor",   label: "VENDOR" },
  { key: "partner",  label: "PARTNER" },
];
const REL_BADGE = {
  customer: { label: "Customer", cls: "bg-blue-50 text-blue-700 border-blue-300 dark:bg-blue-950/40 dark:text-blue-300", Icon: Buildings },
  vendor:   { label: "Vendor",   cls: "bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300", Icon: Toolbox },
  partner:  { label: "Partner",  cls: "bg-emerald-50 text-emerald-700 border-emerald-300 dark:bg-emerald-950/40 dark:text-emerald-300", Icon: ArrowsLeftRight },
};

const EMPTY_CUSTOMER_FORM = { name: "", email: "", phone: "", address: "", proof_recipients_json: [] };
const EMPTY_VENDOR_FORM   = { org_name: "", contact_name: "", contact_email: "", contact_phone: "", send_invite: false };
const EMPTY_INVITE_FORM   = { email: "", customer_org_name: "" };

export default function Organizations() {
  const { user } = useAuth();
  const isContractor = user?.org_type === "contractor";

  // -------------------------------------------------- list state
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ all: 0, customer: 0, vendor: 0, partner: 0, core: 0 });
  const [rel, setRel] = useState("all");
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  // -------------------------------------------------- New menu / drawers
  const [newMenuOpen, setNewMenuOpen] = useState(false);

  // CUSTOMER create/edit drawer — uses the reusable CustomerFormDrawer
  // component. `custEditing` holds either null (create) or the full customer
  // object being edited (so the reusable drawer can hydrate its fields).
  const [custDrawer, setCustDrawer] = useState(false);
  const [custEditing, setCustEditing] = useState(null);

  // VENDOR create drawer
  const [vendDrawer, setVendDrawer] = useState(false);
  const [vendForm, setVendForm] = useState(EMPTY_VENDOR_FORM);
  const [vendFormTags, setVendFormTags] = useState([]);
  const [vendBusy, setVendBusy] = useState(false);
  const [vendErr, setVendErr] = useState("");

  // INVITE CUSTOMER ORG drawer
  const [inviteDrawer, setInviteDrawer] = useState(false);
  const [inviteForm, setInviteForm] = useState(EMPTY_INVITE_FORM);
  const [inviteResult, setInviteResult] = useState(null);
  const [inviteErr, setInviteErr] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  // Combined detail drawer (for plain external customers / vendor-only rows)
  const [detail, setDetail] = useState(null); // { row, customer, vendor, tab: 'customer' | 'vendor' }

  // OrgAdminDrawer state — only opened when the clicked row points at a real
  // partner org (`partner_org_id` is set). External-only customer rows still
  // open the lightweight `detail` drawer above.
  const [orgAdminOpen, setOrgAdminOpen] = useState(null); // { orgId, orgName } | null

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = new URLSearchParams();
      if (rel !== "all") params.set("relationship", rel);
      if (q) params.set("q", q);
      const r = await api.get(`/network?${params}`);
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      setErr(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, [rel, q]);

  useEffect(() => { load(); }, [rel]);
  useEffect(() => { const t = setTimeout(load, 280); return () => clearTimeout(t); }, [q]);

  // ESC closes the + NEW dropdown (nice keyboard UX).
  useEffect(() => {
    if (!newMenuOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setNewMenuOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [newMenuOpen]);

  // ------------------------------------------------------------------
  // CUSTOMER drawer logic (create or edit). All form state + save logic is
  // owned by the reusable <CustomerFormDrawer>. We only manage open/close
  // and the edit-target hydration.
  // ------------------------------------------------------------------
  const openCreateCustomer = () => {
    setCustEditing(null);
    setCustDrawer(true);
    setNewMenuOpen(false);
  };

  const openEditCustomer = async (customerId) => {
    try {
      const r = await api.get(`/customers?paginate=false`);
      const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      const c = list.find((x) => x.id === customerId);
      if (!c) { toast.error("Customer not found."); return; }
      setCustEditing(c);
      setCustDrawer(true);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const deleteCustomer = async (customerId, displayName) => {
    if (!confirm(`Delete customer "${displayName}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/customers/${customerId}`);
      setDetail(null);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // ------------------------------------------------------------------
  // VENDOR create logic
  // ------------------------------------------------------------------
  const openCreateVendor = () => {
    setVendForm(EMPTY_VENDOR_FORM);
    setVendFormTags([]);
    setVendErr("");
    setVendDrawer(true);
    setNewMenuOpen(false);
  };

  const saveVendor = async () => {
    setVendBusy(true); setVendErr("");
    try {
      await api.post("/vendors", { ...vendForm, tag_ids: vendFormTags.map((t) => t.id) });
      setVendDrawer(false);
      setVendForm(EMPTY_VENDOR_FORM); setVendFormTags([]);
      load();
    } catch (e) {
      setVendErr(formatErr(e));
    } finally {
      setVendBusy(false);
    }
  };

  // ------------------------------------------------------------------
  // INVITE CUSTOMER ORG (one-click invite-an-org flow)
  // ------------------------------------------------------------------
  const openInviteCustomer = () => {
    setInviteForm(EMPTY_INVITE_FORM);
    setInviteResult(null); setInviteErr(""); setCopied(false);
    setInviteDrawer(true);
    setNewMenuOpen(false);
  };

  const sendInviteCustomer = async () => {
    setInviteErr(""); setInviteBusy(true);
    try {
      const r = await api.post("/orgs/invitations", {
        email: inviteForm.email,
        invite_type: "customer_org",
        customer_org_name: inviteForm.customer_org_name,
      });
      const url = `${window.location.origin}/accept-invite/${r.data.token}`;
      setInviteResult({ url, ...r.data });
    } catch (e) {
      setInviteErr(formatErr(e));
    } finally {
      setInviteBusy(false);
    }
  };

  const copyInviteUrl = async (url) => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* clipboard blocked */ }
  };

  const resetInvite = () => {
    setInviteDrawer(false);
    setInviteForm(EMPTY_INVITE_FORM);
    setInviteResult(null); setInviteErr(""); setCopied(false);
  };

  // ------------------------------------------------------------------
  // Combined DETAIL drawer (row click) — fetches full customer & vendor
  // records by their respective IDs when present.
  // ------------------------------------------------------------------
  const openDetail = async (row) => {
    // For rows that point at a real partner org, open the rich
    // OrgAdminDrawer (Users / Locations / Departments).
    if (row.partner_org_id) {
      setOrgAdminOpen({ orgId: row.partner_org_id, orgName: row.name });
      return;
    }
    // Otherwise (plain external customer / vendor-only), fall back to the
    // simple combined customer-+-vendor detail drawer.
    setDetail({ row, customer: null, vendor: null, tab: row.customer_id ? "customer" : "vendor", loading: true });
    try {
      const [custList, vendList] = await Promise.all([
        row.customer_id
          ? api.get(`/customers?paginate=false`).then((r) => (Array.isArray(r.data) ? r.data : (r.data?.items || [])))
          : Promise.resolve([]),
        row.vendor_link_id
          ? api.get(`/vendors`).then((r) => r.data || [])
          : Promise.resolve([]),
      ]);
      const customer = row.customer_id ? custList.find((x) => x.id === row.customer_id) || null : null;
      const vendor = row.vendor_link_id ? vendList.find((x) => x.id === row.vendor_link_id) || null : null;
      setDetail({ row, customer, vendor, tab: customer ? "customer" : "vendor", loading: false });
    } catch (e) {
      toast.error(formatErr(e));
      setDetail(null);
    }
  };

  // Vendor row actions inside the detail drawer
  const vendorToggleActive = async (v) => {
    try {
      await api.patch(`/vendors/${v.id}`, { is_active: !v.is_active });
      load();
      setDetail((d) => (d ? { ...d, vendor: { ...d.vendor, is_active: !v.is_active } } : d));
    } catch (e) { toast.error(formatErr(e)); }
  };
  const vendorSendInvite = async (v) => {
    if (!confirm(`Send invite to ${v.vendor_email || v.vendor_name}?`)) return;
    try { await api.post(`/vendors/${v.id}/invite`); load(); await openDetail(detail.row); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const vendorCancelInvite = async (v) => {
    if (!confirm(`Cancel pending invite for ${v.vendor_name}?`)) return;
    try { await api.post(`/vendors/${v.id}/cancel-invite`); load(); await openDetail(detail.row); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const vendorDelete = async (v) => {
    if (!confirm(`Remove ${v.vendor_name} from your vendor list?`)) return;
    try { await api.delete(`/vendors/${v.id}`); setDetail(null); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const vendorCopyInviteUrl = async (v) => {
    if (!v.invite_url) return;
    try { await navigator.clipboard.writeText(v.invite_url); }
    catch { toast.error("Copy failed — your browser blocked clipboard access."); }
  };

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <Page
      title="Organizations"
      subtitle="RECORDS"
      breadcrumbs={[{ label: "Dashboard", to: "/" }, "Organizations"]}
      testid="organizations-page"
      loading={loading && items.length === 0}
      loadingSkeleton={
        <div className="space-y-3">
          <Skeleton.Card />
          <Skeleton.Card />
          <Skeleton.Card />
        </div>
      }
      actions={isContractor ? null : (
          <div className="relative">
            <button
              className="btn btn-primary"
              onClick={() => setNewMenuOpen((v) => !v)}
              data-testid="organizations-new-btn"
              type="button"
            >
              <Plus size={14} /> NEW <CaretDown size={12} />
            </button>
            {newMenuOpen && (
              <>
                <div
                  className="fixed inset-0 z-[80]"
                  onClick={() => setNewMenuOpen(false)}
                  data-testid="organizations-new-menu-backdrop"
                />
                <div
                  className="absolute right-0 mt-1 w-56 surface z-[81] py-1"
                  data-testid="organizations-new-menu"
                >
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-muted/40 flex items-center gap-2"
                    onClick={openCreateCustomer}
                    data-testid="organizations-new-customer"
                  >
                    <Buildings size={14} /> Add Customer
                  </button>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-muted/40 flex items-center gap-2"
                    onClick={openCreateVendor}
                    data-testid="organizations-new-vendor"
                  >
                    <Toolbox size={14} /> Add Vendor
                  </button>
                  <div className="border-t border-border my-1" />
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-muted/40 flex items-center gap-2"
                    onClick={openInviteCustomer}
                    data-testid="organizations-invite-customer"
                  >
                    <EnvelopeSimple size={14} /> Invite Customer Org
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      >

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="organizations-filter-chips">
        {REL_CHIPS.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setRel(c.key)}
            className={`btn btn-sm ${rel === c.key ? "btn-primary" : "btn-secondary"}`}
            data-testid={`organizations-filter-${c.key}`}
          >
            {c.label}
            <span className="ml-1.5 text-[10px] opacity-80 mono">
              {c.key === "all" ? counts.all : (counts[c.key] || 0)}
            </span>
          </button>
        ))}
      </div>

      <ListControls
        testid="organizations-controls"
        q={q}
        onQ={setQ}
        placeholder="Search by name or email…"
      />

      {err && (
        <div className="surface p-3 text-xs text-destructive" data-testid="organizations-error">
          {err}
        </div>
      )}

      <div className="surface">
        <table className="dense">
          <thead>
            <tr>
              <th>NAME</th>
              <th>RELATIONSHIP</th>
              <th>EMAIL</th>
              <th>PHONE</th>
              <th>SINCE</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 && (
              <tr><td colSpan="5" className="text-center text-muted-foreground py-12">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan="5" className="text-center text-muted-foreground py-12" data-testid="organizations-empty">
                  No organizations match.
                </td>
              </tr>
            )}
            {items.map((row) => {
              const badge = REL_BADGE[row.relationship_type] || REL_BADGE.customer;
              const RowIcon = badge.Icon;
              const key = `${row.relationship_type}-${row.partner_org_id || row.customer_id || row.vendor_link_id}`;
              return (
                <tr
                  key={key}
                  className={row.is_core ? "border-l-4 border-l-primary" : ""}
                  data-testid={`org-row-${row.partner_org_id || row.customer_id || row.vendor_link_id}`}
                >
                  <td>
                    <button
                      type="button"
                      className="link-cell font-semibold flex items-center gap-2 text-left"
                      onClick={() => openDetail(row)}
                      data-testid={`org-row-open-${row.partner_org_id || row.customer_id || row.vendor_link_id}`}
                    >
                      {row.is_core && (
                        <Star size={12} weight="fill" className="text-primary flex-shrink-0" title="Core LogBook user" />
                      )}
                      <span>{row.name || "—"}</span>
                    </button>
                  </td>
                  <td>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-wide ${badge.cls}`}
                      data-testid={`org-row-rel-${row.relationship_type}-${row.partner_org_id || row.customer_id || row.vendor_link_id}`}
                    >
                      <RowIcon size={10} />
                      {badge.label}
                    </span>
                  </td>
                  <td className="text-xs">{row.email || "—"}</td>
                  <td className="mono text-xs">{row.phone || "—"}</td>
                  <td className="text-xs">{fmtDate(row.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* ============ Combined DETAIL drawer ============================= */}
      <Drawer
        open={!!detail}
        onClose={() => setDetail(null)}
        size="wide"
        title={detail?.row?.name || ""}
        testid="organizations-detail-drawer"
      >
        {detail && detail.loading && (
          <div className="text-sm text-muted-foreground py-6 text-center">Loading…</div>
        )}
        {detail && !detail.loading && (
          <>
            {/* Tabs (only when both sides present) */}
            {detail.customer && detail.vendor && (
              <div className="flex gap-1 border-b border-border mb-4" data-testid="org-detail-tabs">
                <button
                  type="button"
                  className={`btn btn-sm ${detail.tab === "customer" ? "btn-primary" : "btn-secondary"}`}
                  onClick={() => setDetail((d) => ({ ...d, tab: "customer" }))}
                  data-testid="org-detail-tab-customer"
                >
                  <Buildings size={12} /> Customer Side
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${detail.tab === "vendor" ? "btn-primary" : "btn-secondary"}`}
                  onClick={() => setDetail((d) => ({ ...d, tab: "vendor" }))}
                  data-testid="org-detail-tab-vendor"
                >
                  <Toolbox size={12} /> Vendor Side
                </button>
              </div>
            )}

            {/* Customer panel */}
            {detail.customer && (detail.tab === "customer" || !detail.vendor) && (
              <div className="space-y-3" data-testid="org-detail-customer-panel">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">Customer record</div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => { openEditCustomer(detail.customer.id); setDetail(null); }}
                      data-testid="org-detail-edit-customer"
                    >
                      EDIT
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm text-destructive"
                      onClick={() => deleteCustomer(detail.customer.id, detail.customer.name)}
                      data-testid="org-detail-delete-customer"
                    >
                      DELETE
                    </button>
                  </div>
                </div>
                <Field label="NAME"><div className="text-sm">{detail.customer.name || "—"}</div></Field>
                <Field label="EMAIL"><div className="text-sm">{detail.customer.email || "—"}</div></Field>
                <Field label="PHONE"><div className="text-sm mono">{detail.customer.phone || "—"}</div></Field>
                <Field label="ADDRESS"><div className="text-sm">{detail.customer.address || "—"}</div></Field>
                <Field label="PROOF-OF-SERVICE RECIPIENTS">
                  <div className="text-xs">
                    {(detail.customer.proof_recipients_json || []).length === 0 ? (
                      <span className="text-muted-foreground">None configured</span>
                    ) : (
                      <ul className="space-y-0.5">
                        {detail.customer.proof_recipients_json.map((r, i) => (
                          <li key={i} className="mono">{r.name || r.email || JSON.stringify(r)}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </Field>
                <Field label="TAGS">
                  <TagChipList tags={detail.customer.tags || []} max={10} />
                </Field>
              </div>
            )}

            {/* Vendor panel */}
            {detail.vendor && (detail.tab === "vendor" || !detail.customer) && (
              <div className="space-y-3" data-testid="org-detail-vendor-panel">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">Vendor link</div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className={`btn btn-sm ${detail.vendor.is_active ? "btn-secondary" : "btn-primary"}`}
                      onClick={() => vendorToggleActive(detail.vendor)}
                      data-testid="org-detail-vendor-toggle-active"
                    >
                      {detail.vendor.is_active ? "DEACTIVATE" : "ACTIVATE"}
                    </button>
                    {detail.vendor.claim_status === "pending" ? (
                      <>
                        {detail.vendor.invite_url && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-sm"
                            onClick={() => vendorCopyInviteUrl(detail.vendor)}
                            data-testid="org-detail-vendor-copy-url"
                          ><Copy size={12} /> COPY URL</button>
                        )}
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm"
                          onClick={() => vendorSendInvite(detail.vendor)}
                          data-testid="org-detail-vendor-resend"
                        ><ArrowsClockwise size={12} /> RESEND</button>
                        <button
                          type="button"
                          className="btn btn-ghost btn-sm text-destructive"
                          onClick={() => vendorCancelInvite(detail.vendor)}
                          data-testid="org-detail-vendor-cancel"
                        ><XIcon size={12} /> CANCEL</button>
                      </>
                    ) : detail.vendor.claim_status !== "claimed" ? (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => vendorSendInvite(detail.vendor)}
                        data-testid="org-detail-vendor-send-invite"
                      ><EnvelopeSimple size={12} /> {detail.vendor.claim_status === "expired" ? "RE-INVITE" : "SEND INVITE"}</button>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm text-destructive"
                      onClick={() => vendorDelete(detail.vendor)}
                      data-testid="org-detail-vendor-delete"
                    >
                      REMOVE
                    </button>
                  </div>
                </div>
                <Field label="VENDOR NAME"><div className="text-sm">{detail.vendor.vendor_name || "—"}</div></Field>
                <Field label="CONTACT NAME"><div className="text-sm">{detail.vendor.vendor_contact_name || "—"}</div></Field>
                <Field label="CONTACT EMAIL"><div className="text-sm">{detail.vendor.vendor_email || "—"}</div></Field>
                <Field label="STATUS">
                  <div className="text-xs flex items-center gap-2">
                    <span className={`badge ${detail.vendor.is_active ? "badge-green" : "badge-gray"}`}>
                      {detail.vendor.is_active ? "ACTIVE" : "INACTIVE"}
                    </span>
                    {detail.vendor.claim_status === "claimed" && (
                      <span className="inline-flex items-center gap-1 text-green-600">
                        <CheckCircle size={12} weight="fill" /> CLAIMED
                        {detail.vendor.invite_accepted_at && (
                          <span className="text-muted-foreground">· {fmtDate(detail.vendor.invite_accepted_at)}</span>
                        )}
                      </span>
                    )}
                    {detail.vendor.claim_status === "pending" && (
                      <span className="inline-flex items-center gap-1 text-yellow-700">
                        <Clock size={12} weight="fill" /> PENDING · {fmtDate(detail.vendor.invited_at)}
                      </span>
                    )}
                  </div>
                </Field>
                <Field label="TAGS">
                  <TagChipList tags={detail.vendor.tags || []} max={10} />
                </Field>
              </div>
            )}

            {/* External (no real org) customer fallback message */}
            {!detail.customer && !detail.vendor && (
              <div className="text-sm text-muted-foreground py-4">No detail records found.</div>
            )}
          </>
        )}
      </Drawer>

      {/* ============ OrgAdminDrawer (rich) for partner-linked rows ====== */}
      <OrgAdminDrawer
        open={!!orgAdminOpen}
        onClose={() => { setOrgAdminOpen(null); load(); }}
        orgId={orgAdminOpen?.orgId}
        orgName={orgAdminOpen?.orgName}
      />

      {/* ============ Customer create/edit drawer (reusable) ============ */}
      <CustomerFormDrawer
        open={custDrawer}
        onClose={() => { setCustDrawer(false); setCustEditing(null); }}
        customer={custEditing}
        onSaved={() => { load(); }}
      />

      {/* ============ Vendor create drawer =============================== */}
      <Drawer
        open={vendDrawer}
        onClose={() => setVendDrawer(false)}
        title="New vendor"
        size="narrow"
        testid="new-vendor-drawer"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setVendDrawer(false)}>CANCEL</button>
            <button className="btn btn-primary" onClick={saveVendor} disabled={vendBusy || !vendForm.org_name.trim()} data-testid="save-vendor-btn">
              {vendBusy ? "SAVING…" : "ADD VENDOR"}
            </button>
          </>
        }
      >
        <Field label="VENDOR ORG NAME">
          <input className="input" value={vendForm.org_name}
            onChange={(e) => setVendForm({ ...vendForm, org_name: e.target.value })}
            placeholder="e.g. ACME Process Servers" autoFocus
            data-testid="vendor-org-name" />
        </Field>
        <Field label="PRIMARY CONTACT NAME">
          <input className="input" value={vendForm.contact_name}
            onChange={(e) => setVendForm({ ...vendForm, contact_name: e.target.value })}
            placeholder="Jane Doe" data-testid="vendor-contact-name" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CONTACT EMAIL">
            <input className="input" type="email" value={vendForm.contact_email}
              onChange={(e) => setVendForm({ ...vendForm, contact_email: e.target.value })}
              data-testid="vendor-contact-email" />
          </Field>
          <Field label="CONTACT PHONE">
            <input className="input" value={vendForm.contact_phone}
              onChange={(e) => setVendForm({ ...vendForm, contact_phone: e.target.value })}
              data-testid="vendor-contact-phone" />
          </Field>
        </div>
        <TagPicker
          category="vendor"
          value={vendFormTags}
          onChange={setVendFormTags}
          testid="new-vendor-tag-picker"
        />
        <label className="flex items-center gap-2 mt-4 cursor-pointer" data-testid="vendor-invite-toggle-label">
          <input type="checkbox" checked={vendForm.send_invite}
            onChange={(e) => setVendForm({ ...vendForm, send_invite: e.target.checked })}
            data-testid="vendor-send-invite-toggle" />
          <span className="text-sm">Send an invite link to this vendor now</span>
        </label>
        {vendErr && (
          <div className="text-xs text-destructive mt-3 flex items-center gap-1" data-testid="vendor-form-error">
            <Warning size={14} /> {vendErr}
          </div>
        )}
      </Drawer>

      {/* ============ INVITE CUSTOMER ORG drawer ========================= */}
      <Drawer
        open={inviteDrawer}
        onClose={resetInvite}
        title={inviteResult ? "Invite created" : "Invite a new customer"}
        size="narrow"
        testid="invite-customer-drawer"
        footer={inviteResult ? (
          <button className="btn btn-primary" onClick={resetInvite} data-testid="invite-done-btn">DONE</button>
        ) : (
          <>
            <button className="btn btn-secondary" onClick={resetInvite}>CANCEL</button>
            <button
              className="btn btn-primary"
              onClick={sendInviteCustomer}
              disabled={inviteBusy || !inviteForm.email.trim() || !inviteForm.customer_org_name.trim()}
              data-testid="invite-customer-submit"
            >
              {inviteBusy ? "CREATING…" : "CREATE INVITE"}
            </button>
          </>
        )}
      >
        {!inviteResult && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              We&apos;ll generate a one-time invite link. The recipient picks a password, and a brand-new customer org is created and auto-linked to yours.
            </p>
            <Field label="CUSTOMER COMPANY NAME">
              <input className="input" value={inviteForm.customer_org_name}
                onChange={(e) => setInviteForm({ ...inviteForm, customer_org_name: e.target.value })}
                placeholder="Acme Corp." data-testid="invite-customer-name-input" autoFocus />
            </Field>
            <Field label="ADMIN EMAIL">
              <input className="input" type="email" value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                placeholder="admin@acme.com" data-testid="invite-customer-email-input" />
            </Field>
            {inviteErr && <div className="text-xs text-destructive" data-testid="invite-customer-error">{inviteErr}</div>}
          </div>
        )}
        {inviteResult && (
          <div className="space-y-3" data-testid="invite-customer-success">
            <div className="flex items-center gap-2 text-green-600">
              <CheckCircle size={20} weight="fill" />
              <span className="font-medium">Invite link ready — expires in 7 days.</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Share this URL with <strong>{inviteResult.email}</strong>. On accept, <strong>{inviteForm.customer_org_name}</strong> will be provisioned and linked to your org.
            </p>
            <div className="flex gap-2">
              <input readOnly className="input flex-1 mono text-xs"
                value={inviteResult.url}
                onFocus={(e) => e.target.select()}
                data-testid="invite-customer-url" />
              <button className="btn btn-secondary" onClick={() => copyInviteUrl(inviteResult.url)} data-testid="invite-customer-copy">
                <Copy size={14} /> {copied ? "COPIED" : "COPY"}
              </button>
            </div>
          </div>
        )}
      </Drawer>
    </Page>
  );
}
